﻿import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Check, Circle, Home, CalendarDays, FileStack, Plus, X, Globe, Image as ImageIcon, LockKeyhole, UnlockKeyhole } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { makeDogStateKey } from "@/lib/dogIdentity";
import { useSubscription } from '@/context/SubscriptionContext';
import { useToast } from '@/components/ui/use-toast';
import { useTheme } from '@/context/ThemeContext';

export function ActionBar({ 
  onClearAll, 
  onCheckAll, 
  onGoToToday, 
  onDateChange, 
  currentDate,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  onPdfUpload,
  onClearAllProfiles,
  dayDogs = [],
  completedDogs = {},
  selectedDayKey = '',
  requireConfirmation = false,
  selectedUser = '',
  profiles = [],
  isReadOnly = false,
  reorderUnlocked = false,
  setReorderUnlocked = null
}) {
  const [isManageDialogOpen, setIsManageDialogOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  // 'mark-all' | 'unmark-all' | null
  const [pendingBulkAction, setPendingBulkAction] = useState(null);
  const { permissions } = useSubscription();
  const { toast } = useToast();
  const { theme } = useTheme();
  const lockStrokeWidth = theme === 'dark' ? 1.4 : 1.8;
  // Obtener perfil activo para leer webUrl e imageUrl
  const activeProfile = profiles?.find(p => (p.id ?? p.name) === selectedUser);
  const activeWebUrl = activeProfile?.webUrl || '';
  const activeImageUrl = activeProfile?.imageUrl || '';
  // Normalizar URL: si no tiene protocolo, añade https://
  const openUrl = (url) => {
    if (!url) return;
    const urlMatch = url.match(/https?:\/\/\S+/i);
    const extracted = urlMatch ? urlMatch[0] : url.trim();
    const normalized = /^https?:\/\//i.test(extracted) ? extracted : `https://${extracted}`;
    window.open(normalized, '_blank');
  };

  const handleOpenWeb = () => {
    if (!permissions.canUseQuickWeb) {
      toast({
        title: "Función Premium",
        description: "La web de acceso rápido está disponible en planes Premium.",
        variant: "destructive",
      });
      return;
    }
    if (!activeWebUrl) {
      toast({
        title: "Sin URL configurada",
        description: "Para activar esta función, edítala en la sección de configuración.",
      });
      return;
    }
    openUrl(activeWebUrl);
  };

  const handleOpenImage = () => {
    if (!permissions.canUseQuickImage) {
      toast({
        title: "Función Premium",
        description: "La imagen de acceso rápido está disponible en planes Premium.",
        variant: "destructive",
      });
      return;
    }
    if (!activeImageUrl) {
      toast({
        title: "Sin imagen configurada",
        description: "Para activar esta función, edítala en la sección de configuración.",
      });
      return;
    }
    openUrl(activeImageUrl);
  };

  const buttonClassName = "btn-action bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 h-10 w-10 rounded-full flex items-center justify-center p-0 border border-[#D0D7DC]";
  const iconClassName = "h-5 w-5";

  // Derivar allMarked del estado real: todos los perros del día están marcados
  // Fase 2: clave de estado por identidad estable (id con fallback legacy
  // por nombre), para no perder el estado "marcado" de clientes con id.
  const allMarked = dayDogs.length > 0 &&
    dayDogs.every(dog => completedDogs[makeDogStateKey(selectedDayKey, dog)]);

  const handleToggleAll = () => {
    if (isReadOnly) return;
    if (!requireConfirmation) {
      // Sin confirmación: comportamiento directo
      if (allMarked) {
        onClearAll();
      } else {
        onCheckAll();
      }
      return;
    }

    // Con confirmación activa:
    if (pendingBulkAction) {
      // Ya hay pendencia → cancelar sin ejecutar
      setPendingBulkAction(null);
      return;
    }

    // No hay pendencia → mostrar confirmación
    setPendingBulkAction(allMarked ? 'unmark-all' : 'mark-all');
  };

  const handleConfirmBulk = (e) => {
    e.stopPropagation();
    if (pendingBulkAction === 'unmark-all') {
      onClearAll();
    } else {
      onCheckAll();
    }
    setPendingBulkAction(null);
  };

  const handleCancelBulk = (e) => {
    e.stopPropagation();
    setPendingBulkAction(null);
  };

  const handleManageClick = () => {
    setIsManageDialogOpen(true);
  };

  const handleSubirPdf = () => {
    if (isReadOnly) return;
    setIsManageDialogOpen(false);
    onPdfUpload();
  };

  const handleBorrarConfirm = () => {
    setIsManageDialogOpen(false);
    setIsDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (isReadOnly) return;
    setIsDeleteConfirmOpen(false);
    onClearAllProfiles();
  };

  const handleOpenAddDog = () => {
    if (isReadOnly) return;
    document.getElementById("add-dog-dialog-trigger")?.click();
  };

   return (
     <div
       className="flex items-center justify-center w-full py-0.5"
       onClick={() => { if (pendingBulkAction) setPendingBulkAction(null); }}
     >

       {/* Grupo izquierdo: Candado + Web + Imagen */}
       {!isReadOnly && (
       <div className="flex items-center gap-2">
        {/* Candado de reordenación */}
         {setReorderUnlocked && (
           <Button
             onClick={(e) => { e.stopPropagation(); setReorderUnlocked(prev => !prev); }}
             className="h-10 w-10 rounded-full shadow-md p-0 flex items-center justify-center border bg-muted border-primary hover:bg-primary/5 relative"
             aria-label={reorderUnlocked ? "Bloquear reordenación" : "Desbloquear reordenación"}
             title={reorderUnlocked ? "Candado abierto: arrastra para reordenar" : "Candado cerrado: no puedes arrastrar"}
           >
             <div className="relative w-[25px] h-[25px]">
             <div className="absolute w-[17px] h-[14px] hidden z-0" style={{ bottom: '4px', left: '50%', transform: 'translateX(-50%)' }} />
            {reorderUnlocked ? (
              <UnlockKeyhole className="h-[22.5px] w-[22.5px] absolute inset-0 m-auto z-10 stroke-primary" fill="none" strokeWidth={lockStrokeWidth} />
            ) : (
              <LockKeyhole className="h-[22.5px] w-[22.5px] absolute inset-0 m-auto z-10 stroke-primary" fill="none" strokeWidth={lockStrokeWidth} />
             )}
            </div>
         </Button>
        )}

        {/* Botón Web elegida */}
          <Button
            onClick={handleOpenWeb}
            size="sm"
            className={`h-10 w-10 rounded-full shadow-md p-0 flex items-center justify-center border ml-[5px] ${(!permissions.canUseQuickWeb || !activeWebUrl) ? 'bg-muted border-muted-foreground/30 opacity-50' : 'bg-muted border-primary hover:bg-primary/5'}`}
            title={!permissions.canUseQuickWeb ? "Función Premium" : activeWebUrl ? "Abrir web" : "Sin URL configurada"}
          >
            <Globe className={`h-5 w-5 ${(!permissions.canUseQuickWeb || !activeWebUrl) ? 'text-muted-foreground/60' : 'text-primary'}`} />
          </Button>

          {/* Botón Imagen del perfil */}
          <Button
            onClick={handleOpenImage}
            size="sm"
            className={`h-10 w-10 rounded-full shadow-md p-0 flex items-center justify-center border ${(!permissions.canUseQuickImage || !activeImageUrl) ? 'bg-muted border-muted-foreground/30 opacity-50' : 'bg-muted border-primary hover:bg-primary/5'}`}
            title={!permissions.canUseQuickImage ? "Función Premium" : activeImageUrl ? "Ver imagen del perfil" : "Sin imagen configurada"}
          >
            <ImageIcon className={`h-5 w-5 ${(!permissions.canUseQuickImage || !activeImageUrl) ? 'text-muted-foreground/60' : 'text-primary'}`} />
          </Button>
       </div>
       )}

       {/* Centro: Casa más grande */}
       <Button
         variant="default"
         size="icon"
         onClick={(e) => { e.stopPropagation(); onGoToToday(); }}
         className="h-12 w-12 shrink-0 mx-2 rounded-full shadow-md p-0 flex items-center justify-center border bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 border-[#D0D7DC]"
         aria-label="Hoy"
       >
         <Home className="h-5 w-5" stroke="white" />
       </Button>

      {/* Grupo derecho: Calendario + O + + */}
      <div className="flex items-center gap-2">
         <Popover>
           <PopoverTrigger asChild>
             <Button
               variant="default"
               className="h-10 w-10 rounded-full shadow-md p-0 flex items-center justify-center border bg-muted border-primary hover:bg-primary/5"
               aria-label="Calendario"
               onClick={(e) => e.stopPropagation()}
             >
                <CalendarDays className={`${iconClassName} text-primary`} />
             </Button>
           </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="center">
            <Calendar
              mode="single"
              selected={currentDate}
              onSelect={(date) => {
                if (date) onDateChange(date);
              }}
              initialFocus
            />
          </PopoverContent>
        </Popover>

        {/* Botón azul O confirmación inline (oculto en modo solo lectura) */}
        {!isReadOnly && (pendingBulkAction ? (
          <div
            className="flex items-center gap-1.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-400 dark:border-amber-600 rounded-full px-2.5 py-1"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="text-xs font-semibold text-amber-800 dark:text-amber-200 whitespace-nowrap select-none">
              {pendingBulkAction === 'unmark-all' ? '¿Desmarcar todos?' : '¿Marcar todos?'}
            </span>
            {/* Confirmar */}
            <button
              type="button"
              onClick={handleConfirmBulk}
              className="flex-shrink-0 h-7 w-7 rounded-full bg-green-500 hover:bg-green-600 active:bg-green-700 text-white flex items-center justify-center transition-colors"
              aria-label="Confirmar"
            >
              <Check className="h-3.5 w-3.5" />
            </button>
            {/* Cancelar */}
            <button
              type="button"
              onClick={handleCancelBulk}
              className="flex-shrink-0 h-7 w-7 rounded-full bg-red-500 hover:bg-red-600 active:bg-red-700 text-white flex items-center justify-center transition-colors"
              aria-label="Cancelar"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
           <Button
             variant="default"
             onClick={(e) => { e.stopPropagation(); handleToggleAll(); }}
             className="h-10 w-10 rounded-full shadow-md p-0 flex items-center justify-center border bg-muted border-primary hover:bg-primary/5"
             aria-label={allMarked ? "Desmarcar todo" : "Marcar todo"}
           >
             {allMarked ? (
                <Circle className={`${iconClassName} text-primary`} />
             ) : (
                <Check className={`${iconClassName} text-primary`} />
             )}
           </Button>
        ))}

         {/* Botón + Gestionar clientes en la fila */}
         {!isReadOnly && (
           <Button
             variant="ghost"
             onClick={handleManageClick}
             className="h-10 w-10 rounded-full shadow-md p-0 flex items-center justify-center border bg-muted border-primary hover:bg-primary/5 ml-[6px]"
             aria-label="Gestionar clientes"
           >
             <Plus className="h-[23px] w-[23px] text-primary" strokeWidth={3} />
           </Button>
         )}
      </div>

        {/* Diálogo de gestión principal */}
        <Dialog open={isManageDialogOpen} onOpenChange={setIsManageDialogOpen}>
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle>Gestionar clientes</DialogTitle>
              <DialogDescription>
                Añade nuevos clientes, importa una planificación desde PDF o borra todo el contenido del perfil.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-end landscape-buttons">
              <Button 
                type="button" 
                variant="default" 
                onClick={(e) => { e.stopPropagation(); setIsManageDialogOpen(false); handleOpenAddDog(); }}
                className="landscape-button"
              >
                Añadir cliente
              </Button>
              <Button 
                type="button" 
                variant="default" 
                onClick={handleSubirPdf}
                className="landscape-button"
              >
                Preparar e importar PDF
              </Button>
              <Button 
                type="button" 
                variant="default" 
                onClick={handleBorrarConfirm}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90 landscape-button"
              >
                Borrar contenido del perfil
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setIsManageDialogOpen(false)}
                className="landscape-button"
              >
                Cancelar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

         {/* AlertDialog de confirmación de borrado */}
         <AlertDialog open={isDeleteConfirmOpen} onOpenChange={setIsDeleteConfirmOpen}>
           <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Confirmar borrado del perfil</AlertDialogTitle>
                <AlertDialogDescription>
                  Esto eliminará todo el contenido del perfil actual. Esta acción no se puede deshacer.
                </AlertDialogDescription>
              </AlertDialogHeader>
             <AlertDialogFooter>
               <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleConfirmDelete}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Sí, borrar contenido
                </AlertDialogAction>
             </AlertDialogFooter>
           </AlertDialogContent>
         </AlertDialog>
      </div>
    );
  }

